import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from Supabase session in localStorage
    // Supabase stores the session with a key pattern like: sb-<project-ref>-auth-token
    const storageKeys = Object.keys(localStorage);
    const supabaseAuthKey = storageKeys.find(key => key.includes('sb-') && key.includes('-auth-token'));

    if (supabaseAuthKey) {
      try {
        const authData = JSON.parse(localStorage.getItem(supabaseAuthKey));
        if (authData?.access_token) {
          config.headers.Authorization = `Bearer ${authData.access_token}`;
        }
      } catch (e) {
        console.error('Error parsing auth token:', e);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      console.error('Unauthorized access - token may be expired');
      // You could dispatch an event here to trigger logout
      window.dispatchEvent(new Event('unauthorized'));
    }

    if (error.response?.status === 403 && error.response?.data?.detail?.upgrade_required) {
      // This is an upgrade required error - let components handle it
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // GET request
  get: async (endpoint, config = {}) => {
    const response = await apiClient.get(endpoint, config);
    return response.data;
  },

  // POST request
  post: async (endpoint, data = {}, config = {}) => {
    const response = await apiClient.post(endpoint, data, config);
    return response.data;
  },

  // PUT request
  put: async (endpoint, data = {}, config = {}) => {
    const response = await apiClient.put(endpoint, data, config);
    return response.data;
  },

  // PATCH request
  patch: async (endpoint, data = {}, config = {}) => {
    const response = await apiClient.patch(endpoint, data, config);
    return response.data;
  },

  // DELETE request
  delete: async (endpoint, config = {}) => {
    const response = await apiClient.delete(endpoint, config);
    return response.data;
  },

  // Direct access to axios instance for advanced use cases
  client: apiClient,
};

// Specific API endpoints
export const apiEndpoints = {
  // Auth endpoints
  auth: {
    getCredits: () => api.get('/auth/user/credits'),
  },

  // Stripe endpoints
  stripe: {
    createCheckoutSession: (successUrl, cancelUrl) =>
      api.post('/api/stripe/create-checkout-session', {
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    createPortalSession: (returnUrl) =>
      api.post('/api/stripe/create-portal-session', {
        return_url: returnUrl,
      }),
  },

  // Resume endpoints
  resume: {
    createApplication: (data) => api.post('/resume/create-application', data),
    list: () => api.get('/resume/applications'),
    get: (id) => api.get(`/resume/applications/${id}`),
    update: (id, data) => api.put(`/resume/applications/${id}`, data),
    delete: (id) => api.delete(`/resume/applications/${id}`),
  },

  // Chat/Copilot endpoints
  chat: {
    sendMessage: (data) => api.post('/chat/message', data),
    getHistory: (sessionId) => api.get(`/chat/history/${sessionId}`),
  },

  // Mock Interview endpoints
  mockInterview: {
    start: (data) => api.post('/mock-interview/start', data),
    answer: (sessionId, data) => api.post(`/mock-interview/${sessionId}/answer`, data),
    complete: (sessionId) => api.post(`/mock-interview/${sessionId}/complete`),
  },

  // Interview Prep endpoints
  interviewPrep: {
    generateStarStories: (appId) =>
      api.post(`/interview-prep/applications/${appId}/star-stories/generate`),
    getStarStories: (appId) =>
      api.get(`/interview-prep/applications/${appId}/star-stories`),

    getCompanyResearch: (companyName) =>
      api.get(`/interview-prep/companies/${encodeURIComponent(companyName)}/research`),
    generateCompanyResearch: (companyName) =>
      api.post(`/interview-prep/companies/${encodeURIComponent(companyName)}/research`),

    startPractice: (appId) =>
      api.post(`/interview-prep/applications/${appId}/practice/start`),
    answerPractice: (sessionId, data) =>
      api.post(`/interview-prep/practice/${sessionId}/answer`, data),
    completePractice: (sessionId) =>
      api.post(`/interview-prep/practice/${sessionId}/complete`),
  },

  // Job Discovery endpoints
  jobs: {
    search: (params) => api.get('/jobs/search', { params }),
    get: (id) => api.get(`/jobs/${id}`),
  },
};

export default api;
